import { FormatVizPointLabelParams, FormatVizValueParams, VizValueFormat } from './viz.types';

type PlainVizValueFormat = Exclude<VizValueFormat, 'currency'>;

const COMPACT_OPTIONS: Intl.NumberFormatOptions = {
  notation: 'compact',
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
};

const OPTIONS_BY_VALUE_FORMAT: Record<PlainVizValueFormat, Intl.NumberFormatOptions> = {
  percent: { style: 'percent', maximumFractionDigits: 2 },
  number: {},
};

const COMPACT_OPTIONS_BY_VALUE_FORMAT: Record<PlainVizValueFormat, Intl.NumberFormatOptions> = {
  percent: { style: 'percent', maximumFractionDigits: 1 },
  number: COMPACT_OPTIONS,
};

const formatterCache = new Map<string, Intl.NumberFormat>();

export const getNumberFormatter = (
  locale: string | undefined,
  options: Intl.NumberFormatOptions,
) => {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.NumberFormat(locale, options);
  formatterCache.set(key, formatter);

  return formatter;
};

export const formatVizValue = ({
  value,
  formatMoney,
  format = 'number',
  locale,
}: FormatVizValueParams) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  if (format === 'currency') {
    return formatMoney(value);
  }

  return getNumberFormatter(locale, OPTIONS_BY_VALUE_FORMAT[format]).format(value);
};

export const formatVizValueCompact = ({
  value,
  formatMoney,
  format = 'number',
  locale,
}: FormatVizValueParams) => {
  if (!Number.isFinite(value)) {
    return '';
  }

  if (format === 'currency') {
    return formatMoney(value, COMPACT_OPTIONS);
  }

  return getNumberFormatter(locale, COMPACT_OPTIONS_BY_VALUE_FORMAT[format]).format(value);
};

const ISO_DATE_TIME_PATTERN =
  /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?:[T ]([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?)?$/;

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  ...DATE_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
};

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>();

const getDateFormatter = (locale: string | undefined, options: Intl.DateTimeFormatOptions) => {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  const cached = dateFormatterCache.get(key);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat(locale, options);
  dateFormatterCache.set(key, formatter);

  return formatter;
};

export const formatVizPointLabel = ({ label, locale }: FormatVizPointLabelParams) => {
  const match = ISO_DATE_TIME_PATTERN.exec(label.trim());

  if (!match) {
    return label;
  }

  const [, year, month, day, hour, minute] = match;

  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour) || 0,
    Number(minute) || 0,
  );

  if (date.getDate() !== Number(day)) {
    return label;
  }

  return getDateFormatter(locale, hour ? DATE_TIME_OPTIONS : DATE_OPTIONS).format(date);
};
